/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Form } from '@mairistem/composants';
import {
  Section,
  SectionHeader,
  EtatCivil,
  TypeComponent,
} from '@mairistem/administres-utils';

import { DATE_FORMAT } from '@/constants';

import {
  ChampAdresse,
  ChampLieu,
  ChampProfession,
  ChampsEtatMatrimonial,
  STYLE_TITRE_SECTION,
} from '@/components/Common';

interface InformationsDefuntProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /** Variante d'affichage de l'état civil (cf. `constants/typeComponentByTypeActe`). */
  typeComponent?: TypeComponent;
}

/**
 * Bloc « INFORMATIONS SUR LE DÉFUNT » : état civil, lieu de naissance,
 * domicile, profession, situation matrimoniale et conjoint.
 *
 * ⚠️ Deux choses à savoir :
 *
 * 1. La situation matrimoniale et le conjoint sont portés par l'ACTE
 *    (`ad_df_*`), pas par `ec_acte_individu`. Ils sont saisis ici parce que
 *    c'est du défunt qu'il s'agit ; `buildActeDecesPayload` fait le partage.
 * 2. Les champs de la situation matrimoniale varient selon la situation
 *    choisie : `ChampsEtatMatrimonial` porte cette règle, partagée avec la
 *    modale de la fiche.
 */
const InformationsDefunt = (props: InformationsDefuntProps) => {
  const {
    data,
    errors = {},
    onChange,
    typeComponent = TypeComponent.NAISSANCE,
  } = props;

  /**
   * Validité de la commune du domicile à la date du DÉCÈS, et non à la date de
   * naissance du défunt — que `ChampLieu` retiendrait par défaut, les champs
   * `*Naissance` du défunt étant à plat dans `data`.
   */
  const dateValiditeDomicile = !_.isEmpty(data?.dateDeces)
    ? dayjs(data.dateDeces, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section title="INFORMATIONS SUR LE DÉFUNT" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <EtatCivil
          title="État civil"
          typeComponent={typeComponent}
          individu={data}
          errors={errors}
          onChange={onChange}
        />
        <ChampLieu data={data} errors={errors} onChange={onChange} />
        {/*
          Adresse COMPLÈTE (voie incluse) et non un simple lieu : le domicile
          porte une voie en base. Non obligatoire pour autant — la source est
          l'acte papier.
        */}
        <ChampAdresse
          data={data}
          errors={errors}
          onChange={onChange}
          name="adresseDomicile"
          title="Adresse de domicile"
          dateValidite={dateValiditeDomicile}
        />
        {/* @ts-ignore */}
        {/*
          Deux sections distinctes, aux libellés de la fiche (`DIVERS` et
          `ETAT MATRIMONIAL`) : « Autres informations » mélangeait la profession
          et la situation matrimoniale sous un titre qui ne disait ni l'une ni
          l'autre.
        */}
        {/* @ts-ignore */}
        <SectionHeader
          title="Divers"
          required={false}
          style={STYLE_TITRE_SECTION}
        />
        <div style={{ padding: '1em' }}>
          <Form.Group widths="equal">
            <ChampProfession
              name="profession"
              value={data?.profession}
              errors={errors}
              onChange={onChange}
            />
          </Form.Group>
        </div>
        {/* @ts-ignore */}
        <SectionHeader
          title="État matrimonial"
          required={false}
          style={STYLE_TITRE_SECTION}
        />
        <ChampsEtatMatrimonial
          data={data}
          errors={errors}
          onChange={onChange}
        />
      </Form>
    </Section>
  );
};

export default InformationsDefunt;
